import type { ShopCapability, StoreRole } from "@hobbyshopify/backend-client";
import { type ReactNode, useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Subtree, useObserver, useSubtree } from "subtree.js/react";
import { pick } from "trunk.js";
import { AppController, AppState } from "./app-controller";
import type { AppDeps } from "./app-deps";
import { AppLayout } from "./components/AppLayout";
import { LogOutButton } from "./components/onboarding/OnboardingLayout";
import { ProfileUnavailable } from "./components/ProfileUnavailable";
import { hasCapability } from "./core/primitives/capabilities";
import { consumeStripeReturnToWizard, onboardingPath, stripeConnection } from "./core/primitives/onboarding";
import { DEFAULT_ROUTE } from "./core/primitives/routes";
import { can } from "./core/primitives/store-role";
import { CapabilityGateRoute } from "./pages/capability-gate/capability-gate-route";
import { CardDetailRoute } from "./pages/card-detail/card-detail-route";
import { CardsRoute } from "./pages/cards/cards-route";
import { ChooseWorkspaceRoute } from "./pages/choose-workspace/choose-workspace-route";
import { CustomerCartsRoute } from "./pages/customer-carts/customer-carts-route";
import { CustomerScansRoute } from "./pages/customer-scans/customer-scans-route";
import { DraftsRoute } from "./pages/drafts/drafts-route";
import { EditScanRoute } from "./pages/edit-scan/edit-scan-route";
import { EventEditRoute } from "./pages/event-edit/event-edit-route";
import { EventsRoute } from "./pages/events/events-route";
import { LiveBreakRoute } from "./pages/live-break/live-break-route";
import { LiveStreamRoute } from "./pages/live-stream/live-stream-route";
import { LiveStreamsRoute } from "./pages/live-streams/live-streams-route";
import { LoginRoute } from "./pages/login/login-route";
import { OnboardingLocationsRoute } from "./pages/onboarding-locations/onboarding-locations-route";
import { OnboardingPaymentsRoute } from "./pages/onboarding-payments/onboarding-payments-route";
import { OnboardingSellingRoute } from "./pages/onboarding-selling/onboarding-selling-route";
import { OnboardingShopRoute } from "./pages/onboarding-shop/onboarding-shop-route";
import { OnboardingShowRoute } from "./pages/onboarding-show/onboarding-show-route";
import { OnboardingWebsiteRoute } from "./pages/onboarding-website/onboarding-website-route";
import { PrivacyPolicyPage } from "./pages/privacy-policy/privacy-policy-page";
import { RegisterRoute } from "./pages/register/register-route";
import { ScanCardRoute } from "./pages/scan-card/scan-card-route";
import { SealedRoute } from "./pages/sealed/sealed-route";
import { SellBoxRoute } from "./pages/sell-box/sell-box-route";
import { ShippingRoute } from "./pages/shipping/shipping-route";
import { ShopClaimRoute } from "./pages/shop-claim/shop-claim-route";
import { ShowRoute } from "./pages/show/show-route";
import { StoreSettingsRoute } from "./pages/store-settings/store-settings-route";
import { SentryRoutes } from "./sentry";
import { BroadcastStage } from "./shell/broadcast-stage";
import { ShellController } from "./shell/shell-controller";
import { ShellActions, ShellState } from "./shell/shell-model";

/** Render inside a router (`BrowserRouter` in `main.tsx`). */
export function App({ deps }: { deps: AppDeps }) {
  return (
    <Subtree
      controller={() =>
        new AppController(
          pick(
            deps,
            "client",
            "session",
            "profileRepository",
            "storeRepository",
            "locationsRepository",
            "pendingCartsRepository",
            "providersRepository",
            "capabilitiesRepository",
            "broadcastStudio",
          ),
        )
      }
    >
      <SentryRoutes>
        <Route path="/login" element={<LoginRoute deps={deps} />} />
        <Route path="/register" element={<RegisterRoute deps={deps} />} />
        <Route path="/claim/*" element={<ShopClaimRoute deps={deps} />} />
        {/* Public and unauthenticated on purpose: this URL is what the Shopify
            app listing points at, and app review fetches it without signing in. */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="*" element={<Protected deps={deps} />} />
      </SentryRoutes>
      {/* Above the routes, so navigating does not unmount the camera. A host
          who opens their inventory mid-stream stays on air, and a bar says so
          with the way back. */}
      <BroadcastStage />
    </Subtree>
  );
}

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
    </div>
  );
}

/**
 * Stands between a visitor and the signed-in area. A signed-in user whose
 * profile failed to load is not signed out: sending them to /login would loop,
 * since signing in again cannot fix it.
 */
function Protected({ deps }: { deps: AppDeps }) {
  const state = useSubtree(AppState);
  const observer = useObserver();
  const navigate = useNavigate();
  switch (observer.watch(state.gate)) {
    case "loading":
      return <Spinner />;
    case "profileFailed":
      return <ProfileUnavailable />;
    case "signedOut":
      return <Navigate to="/login" replace />;
    case "signedIn":
      return (
        <Subtree
          key="shell"
          controller={() =>
            new ShellController(
              pick(
                deps,
                "client",
                "session",
                "authHandler",
                "storeRepository",
                "locationsRepository",
                "pendingCartsRepository",
                "providersRepository",
                "capabilitiesRepository",
                "reviewQueueRepository",
                "customersRepository",
                "broadcastStudio",
              ),
              { onSignedOut: () => navigate("/login") },
            )
          }
        >
          <ShellRoutes deps={deps} />
        </Subtree>
      );
  }
}

function ShellRoutes({ deps }: { deps: AppDeps }) {
  const actions = useSubtree(ShellActions);
  const location = useLocation();
  // `?storeId=` anywhere in the signed-in area picks the shop; the controller decides what to make of it.
  const requestedStoreId = new URLSearchParams(location.search).get("storeId");
  useEffect(() => actions.requestStore(requestedStoreId), [actions, requestedStoreId]);

  return (
    <Routes>
      {/* The first-run wizard renders outside the app shell: it is the only
          thing on screen until the account has a usable shop. Each step is its
          own route so it survives a closed tab. */}
      <Route element={<OnboardingGuard />}>
        <Route path="/onboarding" element={<Navigate to="/onboarding/shop" replace />} />
        <Route path="/onboarding/shop" element={<OnboardingShopRoute deps={deps} />} />
        <Route path="/onboarding/selling" element={<OnboardingSellingRoute deps={deps} />} />
        <Route path="/onboarding/show" element={<OnboardingShowRoute deps={deps} />} />
        <Route path="/onboarding/locations" element={<OnboardingLocationsRoute deps={deps} />} />
        <Route path="/onboarding/website" element={<OnboardingWebsiteRoute deps={deps} />} />
        <Route path="/onboarding/payments" element={<OnboardingPaymentsRoute deps={deps} />} />
      </Route>

      <Route
        element={
          <StoreGate deps={deps}>
            <AppLayout />
          </StoreGate>
        }
      >
        <Route path="/cards" element={<CardsRoute deps={deps} />} />
        <Route path="/cards/:cardId" element={<CardDetailRoute deps={deps} />} />
        <Route path="/sealed" element={<SealedRoute deps={deps} />} />
        <Route
          path="/shipping"
          element={
            <RoleGuard allow={(role) => can(role, "hostLive")}>
              <ShippingRoute deps={deps} />
            </RoleGuard>
          }
        />
        {/* The wizard owns shop creation, and old links land on the catalogue. */}
        <Route path="/stores" element={<Navigate to="/cards" replace />} />
        <Route path="/stores/new" element={<Navigate to="/cards" replace />} />
        {/* Inventory and Scans were two lenses on the scan; Cards is the card. */}
        <Route path="/dashboard" element={<Navigate to="/cards" replace />} />
        <Route path="/scans" element={<Navigate to="/cards" replace />} />
        <Route
          path="/customer-scans"
          element={
            <CapabilityGuard deps={deps} capability="POINT_OF_SALE">
              <CustomerScansRoute deps={deps} />
            </CapabilityGuard>
          }
        />
        <Route
          path="/customer-scans/:scanId/edit"
          element={
            <CapabilityGuard deps={deps} capability="POINT_OF_SALE">
              <EditScanRoute deps={deps} kind="customer" />
            </CapabilityGuard>
          }
        />
        <Route path="/scan" element={<ScanCardRoute deps={deps} />} />
        <Route
          path="/drafts"
          element={
            <CapabilityGuard deps={deps} capability="POINT_OF_SALE">
              <DraftsRoute deps={deps} />
            </CapabilityGuard>
          }
        />
        <Route
          path="/scans/:scanId/edit"
          element={
            <CapabilityGuard deps={deps} capability="POINT_OF_SALE">
              <EditScanRoute deps={deps} kind="shop" />
            </CapabilityGuard>
          }
        />
        <Route
          path="/stores/:storeId/settings"
          element={
            <RoleGuard allow={(role) => role !== "STAFF"}>
              <StoreSettingsRoute deps={deps} />
            </RoleGuard>
          }
        />
        <Route
          path="/customer-carts"
          element={
            <CapabilityGuard deps={deps} capability="POINT_OF_SALE">
              <CustomerCartsRoute deps={deps} />
            </CapabilityGuard>
          }
        />
        <Route path="/events" element={<EventsRoute deps={deps} />} />
        <Route
          path="/events/new"
          element={
            <RoleGuard allow={(role) => can(role, "hostEvents")}>
              <EventEditRoute deps={deps} />
            </RoleGuard>
          }
        />
        <Route
          path="/events/:eventId"
          element={
            <RoleGuard allow={(role) => can(role, "hostEvents")}>
              <ShowRoute deps={deps} />
            </RoleGuard>
          }
        />
        <Route
          path="/events/:eventId/edit"
          element={
            <RoleGuard allow={(role) => can(role, "hostEvents")}>
              <EventEditRoute deps={deps} />
            </RoleGuard>
          }
        />
        <Route
          path="/live"
          element={
            <RoleGuard allow={(role) => can(role, "hostLive")}>
              <CapabilityGuard deps={deps} capability="LIVE_BREAKS">
                <LiveStreamsRoute deps={deps} />
              </CapabilityGuard>
            </RoleGuard>
          }
        />
        <Route
          path="/live/sell"
          element={
            <RoleGuard allow={(role) => can(role, "hostLive")}>
              <CapabilityGuard deps={deps} capability="LIVE_BREAKS">
                <SellBoxRoute deps={deps} />
              </CapabilityGuard>
            </RoleGuard>
          }
        />
        <Route path="/live/start" element={<Navigate to="/live/sell" replace />} />
        <Route
          path="/live/breaks/:breakId"
          element={
            <RoleGuard allow={(role) => can(role, "hostLive")}>
              <CapabilityGuard deps={deps} capability="LIVE_BREAKS">
                <LiveBreakRoute deps={deps} />
              </CapabilityGuard>
            </RoleGuard>
          }
        />
        <Route path="*" element={<Navigate to={DEFAULT_ROUTE} replace />} />
      </Route>

      {/* The cockpit renders outside the app shell, like the wizard: running a
          stream is the camera plus the panels over it, and on a phone the menu was
          eating the frame. The studio is not a route of its own any more; the
          old URL still exists because links to it are in the wild. */}
      <Route
        element={
          <StoreGate deps={deps}>
            <Outlet />
          </StoreGate>
        }
      >
        <Route
          path="/live/streams/:streamId"
          element={
            <RoleGuard allow={(role) => can(role, "hostLive")}>
              <CapabilityGuard deps={deps} capability="LIVE_BREAKS">
                <LiveStreamRoute deps={deps} />
              </CapabilityGuard>
            </RoleGuard>
          }
        />
        <Route path="/live/streams/:streamId/broadcast" element={<BroadcastRedirect />} />
        <Route path="/live/shows/:streamId/*" element={<BroadcastRedirect />} />
      </Route>
    </Routes>
  );
}

/**
 * Blocks a route by the caller's role, redirecting to the dashboard rather
 * than showing a page a Staff member has no business on. A `null` role means
 * the shop has not resolved yet, so it never redirects — only a resolved role
 * that fails `allow` does.
 */
function RoleGuard({ allow, children }: { allow: (role: StoreRole) => boolean; children: ReactNode }) {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const role = observer.watch(state.role);
  if (role !== null && !allow(role)) return <Navigate to={DEFAULT_ROUTE} replace />;
  return <>{children}</>;
}

/**
 * Shows the request-access panel in place of a feature HobbyScan has not
 * switched on for the shop. Waits while the answer is out, so the page never
 * fires calls the server would refuse; stands aside if the answer failed.
 */
function CapabilityGuard({
  deps,
  capability,
  children,
}: {
  deps: AppDeps;
  capability: ShopCapability;
  children: ReactNode;
}) {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const capabilities = observer.watch(state.capabilities);
  const failed = observer.watch(state.capabilitiesFailed);
  const has = hasCapability(capabilities, capability);
  if (has === null && !failed) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }
  if (has === false) return <CapabilityGateRoute deps={deps} capability={capability} />;
  return <>{children}</>;
}

/** Old cockpit links (`/broadcast`, and `/live/shows/...` from before streams) still have to land. */
function BroadcastRedirect() {
  const { streamId } = useParams<{ streamId: string }>();
  return <Navigate to={`/live/streams/${streamId}`} replace />;
}

function LoadFailed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app px-4">
      <div className="panel w-full max-w-sm p-6 text-center">
        <p className="text-sm font-semibold text-white">We couldn't load your shop.</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-muted">{message}</p>
        <button type="button" onClick={onRetry} className="btn-primary btn-sm mt-4">
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * Stands between a signed-in user and the app proper: an account missing any
 * part of a working shop is sent to the step that fixes *that* part, rather than
 * shown a dashboard with nothing behind it. The step is answered from the server
 * every time, so the wizard stays gone across devices and reappears only for the
 * aspect that actually went missing.
 */
function StoreGate({ deps, children }: { deps: AppDeps; children: ReactNode }) {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  const gate = observer.watch(state.storeGate);
  const error = observer.watch(state.storeError);
  const store = observer.watch(state.selectedStore);
  const step = observer.watch(state.onboardingStep);
  const needsWorkspace = observer.watch(state.needsWorkspace);

  // Stripe's hosted onboarding returns via the backend, which only knows how to
  // redirect to store settings. If the wizard is what sent the user to Stripe,
  // catch that redirect on arrival and finish the handoff back in step 3.
  const [stripeReturnTarget] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("provider") !== "stripe") return null;
    if (!consumeStripeReturnToWizard()) return null;
    const status = params.get("status");
    const target = onboardingPath("payments", params.get("storeId") ?? undefined);
    return `${target}${status ? `${target.includes("?") ? "&" : "?"}status=${encodeURIComponent(status)}` : ""}`;
  });

  if (stripeReturnTarget) return <Navigate to={stripeReturnTarget} replace />;
  if (gate === "error") return <LoadFailed message={error ?? ""} onRetry={actions.retryStores} />;
  if (gate === "loading") return <Spinner />;
  // Before onboarding: until a shop is picked, the step belongs to whichever shop happened to load first.
  if (needsWorkspace) {
    return (
      <div className="relative">
        <div className="absolute right-4 top-4 z-10">
          <LogOutButton />
        </div>
        <ChooseWorkspaceRoute deps={deps} />
      </div>
    );
  }
  if (step) return <Navigate to={onboardingPath(step, store?.id)} replace />;
  return <>{children}</>;
}

/**
 * The wizard's own guard, and the other half of "a set-up shop never sees
 * onboarding": the gate stops *sending* merchants here, this stops them
 * *arriving* by an old link, a bookmark, or a typed URL. Steps 2 and 3 have
 * nothing to write to until a shop exists, so a direct visit falls back to step 1.
 */
function OnboardingGuard() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const gate = observer.watch(state.storeGate);
  const error = observer.watch(state.storeError);
  const store = observer.watch(state.selectedStore);
  const missing = observer.watch(state.onboardingStep);

  if (gate === "error") return <LoadFailed message={error ?? ""} onRetry={actions.retryStores} />;
  if (gate === "loading") return <Spinner />;
  if (!store && !pathname.endsWith("/shop")) return <Navigate to={onboardingPath("shop")} replace />;

  // The one wizard screen a finished shop may still see: the landing after
  // Stripe's hosted onboarding, which carries `?status=` and explains the outcome.
  const isStripeReturn = pathname.endsWith("/payments") && searchParams.has("status");
  const isClaimReview =
    pathname.endsWith("/shop") && searchParams.get("claimReview") === "1" && searchParams.get("storeId") === store?.id;
  const isClaimOnboarding = searchParams.get("claimOnboarding") === "1" && searchParams.get("storeId") === store?.id;
  // Deferring payments silences the gate; it does not close the step, so the
  // dashboard's "Continue onboarding" link must still land here.
  const paymentsUnfinished = pathname.endsWith("/payments") && stripeConnection(store)?.status !== "ACTIVE";
  if (!missing && !isStripeReturn && !paymentsUnfinished && !isClaimReview && !isClaimOnboarding) {
    const target = store ? `${DEFAULT_ROUTE}?storeId=${encodeURIComponent(store.id)}` : DEFAULT_ROUTE;
    return <Navigate to={target} replace />;
  }

  return (
    <div className="relative">
      <div className="absolute right-4 top-4 z-10">
        <LogOutButton />
      </div>
      <Outlet />
    </div>
  );
}
